/**
 * CustomerModalTabs - Sistema de abas do modal premium de cliente
 * Layout compacto com ícones e contadores
 *
 * @version 1.9.0
 * RELEASE: 2026-10-09 v1.9.0 - Barra de abas passa a usar ModalTabsOverflowBar:
 *            abas excedentes ficam no botão "..." (sem barra de rolagem horizontal)
 * RELEASE: 2026-01-10 v1.0.0 - Criação inicial
 */
import React from 'react';
import {
  LayoutDashboard,
  MessageSquare,
  Briefcase,
  Clock,
  FileSignature,
  Wallet,
  ListChecks,
  Car,
  Megaphone,
  ScrollText,
  Wrench,
} from 'lucide-react';
import { ModalTabsOverflowBar } from '@/components/common/ModalTabsOverflowBar';

// Tipo das abas disponíveis no modal de cliente (v1.1.0: adicionado 'tasks'; v1.5.0: 'vehicles'; v1.6.0: 'campaigns')
export type CustomerModalTab = 'summary' | 'deals' | 'chat' | 'activities' | 'contracts' | 'documents' | 'charges' | 'tasks' | 'vehicles' | 'campaigns' | 'proposals'
  // 2026-10-09: ordens de serviço do cliente
  | 'service_orders';


// Props do componente
interface CustomerModalTabsProps {
  activeTab: CustomerModalTab;
  onTabChange: (tab: CustomerModalTab) => void;
  dealsCount?: number;
  chatCount?: number;
  contractsCount?: number;
  documentsCount?: number;
  chargesCount?: number;
  /** v1.1.0: contador de tarefas vinculadas */
  tasksCount?: number;
  /** 2026-10-09: total de OS do cliente */
  serviceOrdersCount?: number;
  /** 2026-10-09: aba O.S (módulo OS + permissão os_list) */
  serviceOrdersEnabled?: boolean;
  /** v1.5.0: contador de veículos cadastrados */
  vehiclesCount?: number;
  /** v1.8.0: contador de propostas dos negócios do cliente/contato */
  proposalsCount?: number;
  /** Indica se o módulo financeiro está habilitado para o tenant */
  financialEnabled?: boolean;
  /** Indica se o módulo de tarefas está habilitado para o tenant */
  tasksEnabled?: boolean;
  /** v1.5.0: indica se o módulo de veículos está habilitado para o tenant */
  vehiclesEnabled?: boolean;
  /** v1.8.0: indica se o módulo CRM está habilitado (pré-requisito das Propostas) */
  crmEnabled?: boolean;
  /** v1.8.0: permissão de visualização de propostas (crm_proposals.can_view) */
  canViewProposals?: boolean;
  /** v1.2.0: Permissão de visualização da aba financeira (financial_charges.can_view) */
  canViewCharges?: boolean;
  /** v1.3.0: Permissão de visualização da aba contratos (crm_contracts.can_view) */
  canViewContracts?: boolean;
  /**
   * v1.4.0: Modo Contato (contato sem cliente vinculado).
   * Oculta abas que dependem de dados cadastrais do cliente
   * (Resumo, Contratos, Documentos, Financeiro e Atividades).
   */
  isContactMode?: boolean;
}

// Configuração das abas (v1.1.0: adicionado 'tasks'; v1.5.0: 'vehicles')
const tabsConfig: Array<{
  id: CustomerModalTab;
  label: string;
  icon: typeof LayoutDashboard;
  countKey?: 'dealsCount' | 'chatCount' | 'contractsCount' | 'documentsCount' | 'chargesCount' | 'tasksCount' | 'vehiclesCount' | 'proposalsCount' | 'serviceOrdersCount';
}> = [
  { id: 'summary', label: 'Resumo', icon: LayoutDashboard },
  { id: 'deals', label: 'Negócios', icon: Briefcase, countKey: 'dealsCount' },
  { id: 'chat', label: 'Chat', icon: MessageSquare, countKey: 'chatCount' },
  { id: 'vehicles', label: 'Veículos', icon: Car, countKey: 'vehiclesCount' },
  // v1.8.0: propostas comerciais de todos os negócios do cliente/contato
  { id: 'proposals', label: 'Propostas', icon: ScrollText, countKey: 'proposalsCount' },
  { id: 'contracts', label: 'Contratos', icon: FileSignature, countKey: 'contractsCount' },
  { id: 'documents', label: 'Documentos', icon: FileSignature, countKey: 'documentsCount' },
  { id: 'charges', label: 'Financeiro', icon: Wallet, countKey: 'chargesCount' },
  { id: 'tasks', label: 'Tarefas', icon: ListChecks, countKey: 'tasksCount' },
  { id: 'service_orders', label: 'O.S', icon: Wrench, countKey: 'serviceOrdersCount' },
  // v1.6.0: aba de campanhas — sem contador (a consulta só roda ao abrir a aba)
  { id: 'campaigns', label: 'Campanhas', icon: Megaphone },
  { id: 'activities', label: 'Atividades', icon: Clock },
];


/**
 * Sistema de abas do modal (36px)
 */
export function CustomerModalTabs({
  activeTab,
  onTabChange,
  dealsCount,
  chatCount,
  contractsCount,
  documentsCount,
  chargesCount,
  tasksCount,
  vehiclesCount,
  proposalsCount,
  serviceOrdersCount,
  serviceOrdersEnabled = false,
  financialEnabled = false,
  tasksEnabled = false,
  vehiclesEnabled = false,
  crmEnabled = true,
  canViewProposals = true,
  canViewCharges = true,
  canViewContracts = true,
  isContactMode = false
}: CustomerModalTabsProps) {
  const counts = { dealsCount, chatCount, contractsCount, documentsCount, chargesCount, tasksCount, vehiclesCount, proposalsCount, serviceOrdersCount };

  // Abas permitidas no modo Contato (sem cliente vinculado)
  // Contratos/Documentos/Financeiro/Atividades/Resumo dependem do cadastro do cliente
  // v1.5.0: Veículos funciona por contact_id, então é liberado no modo Contato
  // v1.6.0: Campanhas funciona por contact_id, então também é liberado no modo Contato
  // v1.7.0: Resumo liberado no modo Contato (timeline de andamentos por contact_id)
  const CONTACT_MODE_TABS: CustomerModalTab[] = ['summary', 'deals', 'chat', 'tasks', 'vehicles', 'campaigns', 'proposals'];


  // Filtra abas conforme módulos habilitados no tenant + permissões granulares
  // v1.2.0: aba Financeiro respeita financial_charges.can_view
  // v1.3.0: aba Contratos respeita crm_contracts.can_view
  // v1.4.0: modo Contato restringe às abas suportadas apenas por contact_id
  // v1.5.0: aba Veículos só aparece com o módulo habilitado no tenant
  const visibleTabs = tabsConfig.filter(tab => {
    if (isContactMode && !CONTACT_MODE_TABS.includes(tab.id)) return false;
    if (tab.id === 'charges' && (!financialEnabled || !canViewCharges)) return false;
    if (tab.id === 'contracts' && !canViewContracts) return false;
    if (tab.id === 'tasks' && !tasksEnabled) return false;
    if (tab.id === 'vehicles' && !vehiclesEnabled) return false;
    // 2026-10-09: aba O.S exige módulo OS + permissão (fora do modo Contato)
    if (tab.id === 'service_orders' && !serviceOrdersEnabled) return false;
    // v1.8.0: aba Propostas exige módulo CRM + permissão de visualização
    if (tab.id === 'proposals' && (!crmEnabled || !canViewProposals)) return false;
    return true;
  });

  // v1.9.0: renderização delegada ao componente compartilhado com overflow "..."
  // (abas que não cabem na largura ficam na lista suspensa, sem barra de rolagem)
  return (
    <ModalTabsOverflowBar
      items={visibleTabs.map((tab) => ({
        id: tab.id,
        label: tab.label,
        icon: tab.icon,
        count: tab.countKey ? counts[tab.countKey] : undefined,
      }))}
      activeId={activeTab}
      onChange={(id) => onTabChange(id as CustomerModalTab)}
    />
  );
}
